import React, { useState, useEffect } from 'react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Trophy, Calendar, Clock } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

interface Week {
  number: number;
  date: string;
  dateEnd?: string;
  status: 'past' | 'current' | 'future';
  time: string;
  attendance?: 'attending' | 'not-attending' | null;
  isRest?: boolean;
}

export type EliminationPhaseType = 'octavos' | 'cuartos' | 'semifinal' | 'final';

export interface EliminationScheduleConfig {
  isSingleDate: boolean;
  singleDateInfo?: {
    date: string;
    startTime: string;
    endTime: string;
  };
  phaseSchedules?: Record<string, { date: string; time: string }>;
}

interface WeeklyCalendarProps {
  weeks: Week[];
  weeksRemaining: number;
  onWeekClick?: (week: Week) => void;
  onAttendanceChange?: (weekNumber: number, attendance: 'attending' | 'not-attending') => void;
  showPhaseToggle?: boolean;
  currentPhase?: 'regular' | 'elimination';
  onPhaseChange?: (phase: 'regular' | 'elimination') => void;
  eliminationWeeks?: Array<{
    number: number;
    date: string;
    status: 'past' | 'current' | 'future';
    time: string;
    phase: string;
  }>;
  initialAttendance?: Record<number, 'attending' | 'not-attending' | null>;
  isUpdatingAttendance?: boolean;
  eliminationPhase?: EliminationPhaseType;
  eliminationScheduleConfig?: EliminationScheduleConfig;
  hideAttendance?: boolean;
  sport?: string;
}

const WeeklyCalendar = ({ 
  weeks, 
  weeksRemaining, 
  onWeekClick, 
  onAttendanceChange,
  showPhaseToggle = false,
  currentPhase = 'regular',
  onPhaseChange,
  eliminationWeeks = [],
  initialAttendance = {},
  isUpdatingAttendance = false,
  eliminationPhase,
  eliminationScheduleConfig,
  hideAttendance = false,
  sport
}: WeeklyCalendarProps) => {
  const [weekAttendance, setWeekAttendance] = useState<Record<number, 'attending' | 'not-attending' | null>>(initialAttendance);
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  const isMatchOriented = sport === 'padel' || sport === 'pickleball';

  // Sync with initial attendance from database when it changes
  useEffect(() => {
    if (Object.keys(initialAttendance).length > 0) {
      setWeekAttendance(initialAttendance);
    }
  }, [initialAttendance]);
  
  const handleAttendanceChange = (weekNumber: number, attendance: 'attending' | 'not-attending') => {
    setWeekAttendance(prev => ({ ...prev, [weekNumber]: attendance }));
    onAttendanceChange?.(weekNumber, attendance);
  };

  const getAttendanceStatus = (weekNumber: number) => {
    return weekAttendance[weekNumber] || null;
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'past': return 'bg-gray-400 text-white';
      case 'current': return 'bg-green-500 text-white ring-4 ring-green-200';
      case 'future': return 'bg-primary text-primary-foreground';
      default: return 'bg-gray-400 text-white';
    }
  };

  const getClassificationInfo = () => {
    const counts: Record<string, number> = { octavos: 16, cuartos: 8, semifinal: 4, final: 2 };
    const count = counts[eliminationPhase || 'semifinal'] || 4;
    return { count, text: t('league.classifyTop').replace('{count}', String(count)) };
  };

  const getEliminationStructure = (): string[] => {
    const phases: string[] = [];
    switch (eliminationPhase) {
      case 'octavos':
        phases.push(t('league.octavos'), t('league.cuartos'), t('league.semifinales'), t('league.final'));
        break;
      case 'cuartos':
        phases.push(t('league.cuartos'), t('league.semifinales'), t('league.final'));
        break;
      case 'semifinal':
        phases.push(t('league.semifinales'), t('league.final'));
        break;
      case 'final':
        phases.push(t('league.final'));
        break;
      default:
        if (showPhaseToggle) {
          phases.push(t('league.semifinales'), t('league.final'));
        }
        break;
    }
    return phases;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-8">
      <div className={`${isMobile ? 'flex flex-col gap-3' : 'flex items-center justify-between'} mb-4 md:mb-6`}>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>
            {t('league.leagueCalendar')}
          </h2>
        </div>
        
        <div className={`flex items-center ${isMobile ? 'gap-2 flex-wrap justify-center' : 'gap-3'}`}>
          {showPhaseToggle && (
            <div className={`inline-flex bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-1 border border-primary/20 ${isMobile ? 'text-xs' : ''}`}>
              <button
                className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'} rounded-md font-medium transition-all ${
                  currentPhase === 'regular'
                    ? 'bg-white text-blue-600 shadow-sm border border-blue-200'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                }`}
                onClick={() => onPhaseChange?.('regular')}
              >
                {isMobile ? t('league.regular') : t('league.regularPhase')}
              </button>
              <button
                className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'} rounded-md font-medium transition-all ${
                  currentPhase === 'elimination'
                    ? 'bg-white text-orange-600 shadow-sm border border-orange-200'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                }`}
                onClick={() => onPhaseChange?.('elimination')}
              >
                {t('league.elimination')}
              </button>
            </div>
          )}
          
          {currentPhase === 'regular' && (
            <div className={`bg-gradient-to-r from-blue-500 to-green-500 rounded-lg ${isMobile ? 'px-2 py-1' : 'px-3 py-2'}`}>
              <div className="flex items-center gap-2">
                <div className={`text-white ${isMobile ? 'text-xs' : 'text-xs'} font-medium`}>
                  {isMobile ? `${t('league.encountersLabel')}:` : `${t('league.totalEncounters')}:`}
                </div>
                <div className={`text-white ${isMobile ? 'text-sm' : 'text-lg'} font-bold`}>{weeks.filter(w => !w.isRest).length}</div>
              </div>
            </div>
          )}
          
          {currentPhase === 'elimination' && (
            <div className={`bg-gradient-to-r from-orange-500 to-yellow-500 rounded-lg ${isMobile ? 'px-2 py-1' : 'px-3 py-2'}`}>
              <div className="flex items-center gap-2">
                <Trophy className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-white`} />
                <div className={`text-white ${isMobile ? 'text-xs' : 'text-xs'} font-medium`}>
                  {isMobile ? t('league.elimination') : t('league.eliminationPhase')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <ScrollArea className="w-full">
        <div className={`flex space-x-4 pb-4 justify-center`} style={{ minWidth: 'max-content' }}>
          {currentPhase === 'regular' ? (
            weeks.map((week) => (
              <div key={week.number} className="flex flex-col space-y-2">
                {week.isRest ? (
                  <div
                    className={`bg-yellow-400 text-yellow-900 rounded-xl ${isMobile ? 'p-3 w-24' : 'p-4 w-32'} text-center transition-all duration-300 hover:scale-105 hover:shadow-lg transform flex-shrink-0 animate-fade-in`}
                  >
                    <div className={`font-bold ${isMobile ? 'text-sm' : 'text-lg'}`}>
                      {isMobile ? `S${week.number}` : `${t('league.week')} ${week.number}`}
                    </div>
                    <div className={`${isMobile ? 'text-xs' : 'text-sm'} mt-1 opacity-90`}>{week.date}</div>
                    {week.dateEnd && (
                      <div className={`${isMobile ? 'text-xs' : 'text-sm'} opacity-90`}>{week.dateEnd}</div>
                    )}
                    <div className={`${isMobile ? 'text-xs' : 'text-sm'} mt-2 font-semibold`}>☕ {t('league.rest')}</div>
                  </div>
                ) : (
                  <div
                    className={`${getStatusColor(week.status)} rounded-xl ${isMobile ? 'p-3 w-24' : 'p-4 w-32'} text-center cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg transform flex-shrink-0 animate-fade-in`}
                    onClick={() => onWeekClick?.(week)}
                  >
                     {(() => {
                       const displayNumber = sport === 'padel'
                         ? ((week as any).encounterNumber ?? week.number)
                         : week.number;
                       return (
                         <div className={`font-bold ${isMobile ? 'text-sm' : 'text-lg'}`}>
                           {isMatchOriented
                             ? (isMobile ? `E${displayNumber}` : `${t('league.encounter')} ${displayNumber}`)
                             : (isMobile ? `S${displayNumber}` : `${t('league.week')} ${displayNumber}`)}
                         </div>
                       );
                     })()}
                     <div className={`${isMobile ? 'text-xs' : 'text-sm'} mt-1 opacity-90`}>{week.date}</div>
                     {week.dateEnd && (
                       <div className={`${isMobile ? 'text-xs' : 'text-sm'} opacity-90`}>{week.dateEnd}</div>
                     )}
                     <div className={`${isMobile ? 'text-xs' : 'text-sm'} mt-1 opacity-80 font-medium`}>{week.time}</div>
                  </div>
                )}
                
                {!hideAttendance && !week.isRest && (week.status === 'current' || week.status === 'future') && (
                  <div className={`flex flex-col space-y-1 ${isMobile ? 'w-24' : 'w-32'}`}>
                    <div className={`text-center ${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 font-medium`}>
                      {isMobile ? t('league.willYouAttend') : t('league.confirmAttendance')}
                    </div>
                    <Button
                      size={isMobile ? "xs" : "sm"}
                      onClick={() => handleAttendanceChange(week.number, 'attending')}
                      className={`${isMobile ? 'text-xs py-1 h-6' : 'text-xs py-1 h-7'} transition-all duration-200 ${
                        getAttendanceStatus(week.number) === 'attending'
                          ? 'bg-green-600 hover:bg-green-700 text-white shadow-md'
                          : 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200'
                      }`}
                    >
                      <Check className={`${isMobile ? 'h-2 w-2' : 'h-3 w-3'} mr-1`} />
                      {isMobile ? t('common.yes') : t('league.willAttend')}
                    </Button>
                    <Button
                      size={isMobile ? "xs" : "sm"}
                      onClick={() => handleAttendanceChange(week.number, 'not-attending')}
                      className={`${isMobile ? 'text-xs py-1 h-6' : 'text-xs py-1 h-7'} transition-all duration-200 ${
                        getAttendanceStatus(week.number) === 'not-attending'
                          ? 'bg-red-600 hover:bg-red-700 text-white shadow-md'
                          : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
                      }`}
                    >
                      <X className={`${isMobile ? 'h-2 w-2' : 'h-3 w-3'} mr-1`} />
                      {isMobile ? t('common.no') : t('league.wontAttend')}
                    </Button>
                  </div>
                )}
              </div>
            ))
          ) : (
            eliminationWeeks.map((week) => (
              <div key={week.number} className="flex flex-col space-y-2">
                <div
                  className="bg-gradient-to-r from-orange-400 to-yellow-400 rounded-lg p-4 text-center cursor-pointer transition-all duration-200 hover:scale-105 transform flex-shrink-0 w-36 text-white"
                  onClick={() => onWeekClick?.({ ...week, attendance: null })}
                >
                   <div className="font-bold text-lg">{week.phase}</div>
                   <div className="text-sm mt-1">{week.date}</div>
                </div>
                <div className="w-36 text-center">
                  <div className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                    {week.status === 'future' ? t('league.next') : t('league.completed')}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {currentPhase === 'elimination' && (
        <div className="mt-4 space-y-3">
          {getEliminationStructure().length > 0 && (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
              <div className="text-center">
                <div className="text-sm font-semibold text-orange-800 mb-3">
                  📊 {t('league.eliminationStructure')}
                </div>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {getEliminationStructure().map((phase, index, arr) => (
                    <React.Fragment key={phase}>
                      <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                        {phase}
                      </Badge>
                      {index < arr.length - 1 && (
                        <span className="text-orange-400 font-bold">→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {eliminationScheduleConfig && (
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <div className="text-sm font-semibold text-blue-800">
                    {t('league.eliminationSchedule')}
                  </div>
                </div>
                {eliminationScheduleConfig.isSingleDate ? (
                  <div className="text-xs text-blue-700">
                    <div className="font-medium text-sm">
                      {eliminationScheduleConfig.singleDateInfo?.date}
                    </div>
                    <div className="mt-1">
                      {eliminationScheduleConfig.singleDateInfo?.startTime} - {eliminationScheduleConfig.singleDateInfo?.endTime}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5 max-w-xs mx-auto">
                    {Object.entries(eliminationScheduleConfig.phaseSchedules || {}).map(([phase, schedule]) => (
                      <div key={phase} className="text-xs text-blue-700 flex justify-between bg-blue-50 px-3 py-1.5 rounded">
                        <span className="font-medium">{phase}:</span>
                        <span>{schedule.date} - {schedule.time}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Trophy className="h-5 w-5 text-yellow-600 mr-2" />
                <div className="text-sm font-semibold text-yellow-800">
                  {t('league.autoClassification')}
                </div>
              </div>
              <div className="text-xs text-yellow-700">
                {getClassificationInfo().text}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap justify-center mt-6 gap-x-4 gap-y-2 px-4 text-sm">
        {currentPhase === 'regular' ? (
          <>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-400 rounded mr-2"></div>
              <span className="text-gray-600">{t('league.past')}</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
              <span className="text-gray-600">{t('league.current')}</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-primary rounded mr-2"></div>
              <span className="text-gray-600">{t('league.future')}</span>
            </div>
            {weeks.some(w => w.isRest) && (
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-400 rounded mr-2"></div>
                <span className="text-gray-600">{t('league.rest')}</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gradient-to-r from-orange-400 to-yellow-400 rounded mr-2"></div>
            <span className="text-gray-600">{t('league.eliminationPhases')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyCalendar;
